import axiosInstance from "../../utils/axios";
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { AppDispatch } from "../store";

interface SubscriptionInvoice {
  id: number;
  invoiceNumber: string;
  date: string;
  dueAt: string;
  total: number;
  paid: boolean;
  associationId: number;
}

interface SubscriptionInvoiceState {
  invoices: SubscriptionInvoice[];
  allInvoices: any[];
  invoice?: SubscriptionInvoice;
  status: "idle" | "loading" | "failed";
  allInvoicesStatus: "idle" | "loading" | "failed";
}

const initialState: SubscriptionInvoiceState = {
  invoices: [],
  allInvoices: [],
  invoice: undefined,
  status: "idle",
  allInvoicesStatus: "idle",
};

export const fetchSubscriptionInvoices = createAsyncThunk(
  "subscriptionInvoice/fetchSubscriptionInvoices",
  async (associationId: number) => {
    const response = await axiosInstance.get(
      `/subscription-invoices/association/${associationId}`
    );
    return response.data;
  }
);

export const fetchAllSubscriptionInvoices = createAsyncThunk(
  "subscriptionInvoice/fetchAllSubscriptionInvoices",
  async () => {
    const response = await axiosInstance.get("/subscription-invoices");
    return response.data;
  }
);

export const updateSubscriptionInvoicesBatchPaid = createAsyncThunk(
  "subscriptionInvoice/updateSubscriptionInvoicesBatchPaid",
  async (
    { ids, associationId }: { ids: number[]; associationId: number },
    { dispatch }
  ) => {
    await axiosInstance.put(`/subscription-invoices/batch/paid`, {
      ids,
      associationId,
    });
    await dispatch(fetchSubscriptionInvoices(associationId));
    return ids;
  }
);

const subscriptionInvoiceSlice = createSlice({
  name: "subscriptionInvoice",
  initialState,
  reducers: {
    setSubscriptionInvoices(state, action) {
      state.invoices = action.payload;
    },
    setSubscriptionInvoice(state, action) {
      state.invoice = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchSubscriptionInvoices.pending, (state) => {
        state.status = "loading";
      })
      .addCase(fetchSubscriptionInvoices.fulfilled, (state, action) => {
        state.status = "idle";
        state.invoices = action.payload;
      })
      .addCase(fetchSubscriptionInvoices.rejected, (state) => {
        state.status = "failed";
      })
      .addCase(fetchAllSubscriptionInvoices.pending, (state) => {
        state.allInvoicesStatus = "loading";
      })
      .addCase(fetchAllSubscriptionInvoices.fulfilled, (state, action) => {
        state.allInvoicesStatus = "idle";
        state.allInvoices = action.payload;
      })
      .addCase(fetchAllSubscriptionInvoices.rejected, (state) => {
        state.allInvoicesStatus = "failed";
      });
  },
});

export function createSubscriptionInvoice(
  values: Partial<SubscriptionInvoice>,
  associationId: number
) {
  return async (dispatch: AppDispatch) => {
    const response = await axiosInstance.post(`/subscription-invoices`, {
      ...values,
      associationId,
    });
    dispatch(fetchSubscriptionInvoices(associationId));
    return response.data;
  };
}

export function updateSubscriptionInvoice(
  id: number,
  values: Partial<SubscriptionInvoice>
) {
  return async (dispatch: AppDispatch) => {
    const response = await axiosInstance.put(
      `/subscription-invoices/${id}`,
      values
    );
    dispatch(fetchSubscriptionInvoices(values.associationId!));
    return response.data;
  };
}

export function markSubscriptionInvoicePaid(id: number, associationId: number) {
  return async (dispatch: AppDispatch) => {
    await axiosInstance.put(`/subscription-invoices/${id}/mark-paid`);
    dispatch(fetchSubscriptionInvoices(associationId));
  };
}

/**
 * Issues a reminder invoice (original amount + muistutusmaksu, due in 7 days)
 * and emails it to the association's admin. Irreversible — the caller is
 * expected to confirm with the user first.
 */
export function sendSubscriptionInvoiceReminder(id: number) {
  return async (dispatch: AppDispatch) => {
    const response = await axiosInstance.post(
      `/subscription-invoices/${id}/send-reminder`
    );
    dispatch(fetchAllSubscriptionInvoices());
    return response.data;
  };
}

export function deleteSubscriptionInvoice(id: number, associationId: number) {
  return async (dispatch: AppDispatch) => {
    await axiosInstance.delete(`/subscription-invoices/${id}`);
    dispatch(fetchSubscriptionInvoices(associationId));
  };
}

export function deleteSubscriptionInvoicesBatch(
  ids: number[],
  associationId: number
) {
  return async (dispatch: AppDispatch) => {
    await axiosInstance.delete(`/subscription-invoices/batch/delete`, {
      data: ids,
    });
    dispatch(fetchSubscriptionInvoices(associationId));
  };
}

export const { setSubscriptionInvoices, setSubscriptionInvoice } =
  subscriptionInvoiceSlice.actions;

export default subscriptionInvoiceSlice.reducer;
